/**
 * Insurance Summary Wizard
 * Multi-step wizard for creating insurance medical record summaries
 */

import React, { useState } from 'react';
import { Stepper, Step, StepLabel, Paper, Typography, Alert, CircularProgress, Box } from '@mui/material';
import { useInsuranceReport } from '../../hooks/useInsuranceReport';
import type { OutputFormat } from '../../types/insurance';
import PDFUploadStep from './PDFUploadStep';
import FinalizationStep from './FinalizationStep';
import ResultsDisplay from './ResultsDisplay';

interface InsuranceSummaryWizardProps {
  initialReportId?: string;
}

const InsuranceSummaryWizard: React.FC<InsuranceSummaryWizardProps> = ({ initialReportId }) => {
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('standard');

  const {
    currentStep,
    uploadedPDFs,
    reportStatus,
    results,
    error,
    isInitializing,
    uploadPDF,
    finalize,
    pollStatus,
    reset,
    canProceedToFinalize,
    isProcessing,
  } = useInsuranceReport({ initialReportId, outputFormat });

  if (isInitializing) {
    return (
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ mr: 2 }} />
          <Typography>Initializing report...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Insurance Medical Record Summary
      </Typography>

      <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Upload Records</StepLabel>
        </Step>
        <Step>
          <StepLabel>Generate Summary</StepLabel>
        </Step>
        <Step>
          <StepLabel>Results</StepLabel>
        </Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {currentStep === 0 && (
        <PDFUploadStep
          uploadedPDFs={uploadedPDFs}
          onUploadPDF={uploadPDF}
          onFinalize={finalize}
          canProceedToFinalize={canProceedToFinalize}
          isProcessing={isProcessing}
          outputFormat={outputFormat}
          onOutputFormatChange={setOutputFormat}
        />
      )}

      {currentStep === 1 && (
        <FinalizationStep
          reportStatus={reportStatus}
          uploadedPDFs={uploadedPDFs}
          pollStatus={pollStatus}
        />
      )}

      {currentStep === 2 && results && <ResultsDisplay results={results} onReset={reset} />}
    </Paper>
  );
};

export default InsuranceSummaryWizard;
